import React, { useState } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  Collapse,
  Button,
  alpha
} from '@mui/material';
import useResponsive from '../../hooks/useResponsive';
import {
  Menu as MenuIcon,
  Dashboard,
  Analytics,
  Person,
  Settings,
  Logout,
  Notifications,
  ExpandLess,
  ExpandMore,
  CardMembership,
  LocalOffer,
  Event,
  Language,
  Stars,
  Business,
  History,
  Group,
  ConfirmationNumber,
  LocationOn,
  Security,
  VpnKey,
  ContactSupport
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useCompany } from '../../contexts/CompanyContext';
import { useNavigate, useLocation } from 'react-router-dom';
import Logo from './Logo';

const drawerWidth = 240;

interface MenuItemType {
  text: string;
  icon: React.ReactNode;
  path: string;
  hasSubmenu?: boolean;
  adminOnly?: boolean;
  disabled?: boolean;
  feature?: 'membership_enabled' | 'events_enabled' | 'coupons_enabled';
  subItems?: Array<{
    text: string;
    icon: React.ReactNode;
    path: string;
    adminOnly?: boolean;
    feature?: 'membership_enabled' | 'events_enabled' | 'coupons_enabled';
  }>;
}

interface DashboardLayoutProps {
  children: React.ReactNode;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [passManagerOpen, setPassManagerOpen] = useState(false);
  const [eventManagerOpen, setEventManagerOpen] = useState(false);
  const [ticketsManagerOpen, setTicketsManagerOpen] = useState(false);
  const [membershipManagerOpen, setMembershipManagerOpen] = useState(false);
  const [couponManagerOpen, setCouponManagerOpen] = useState(false);
  const [administrationOpen, setAdministrationOpen] = useState(false);
  const [languageAnchorEl, setLanguageAnchorEl] = useState<null | HTMLElement>(null);
  const [currentLanguage, setCurrentLanguage] = useState('sr'); // 'sr' za srpski, 'en' za engleski

  const { user, logout } = useAuth();
  const { features } = useCompany();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { getTouchFriendlySize, getSpacing } = useResponsive();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleProfileMenuClose();
    navigate('/');
  };

  const handlePassManagerToggle = () => {
    setPassManagerOpen(!passManagerOpen);
  };

  const handleEventManagerToggle = () => {
    setEventManagerOpen(!eventManagerOpen);
  };

  const handleTicketsManagerToggle = () => {
    setTicketsManagerOpen(!ticketsManagerOpen);
  };

  const handleMembershipManagerToggle = () => {
    setMembershipManagerOpen(!membershipManagerOpen);
  };

  const handleCouponManagerToggle = () => {
    setCouponManagerOpen(!couponManagerOpen);
  };

  const handleAdministrationToggle = () => {
    setAdministrationOpen(!administrationOpen);
  };

  const handleLanguageMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setLanguageAnchorEl(event.currentTarget);
  };

  const handleLanguageMenuClose = () => {
    setLanguageAnchorEl(null);
  };

  const handleLanguageChange = (language: string) => {
    setCurrentLanguage(language);
    handleLanguageMenuClose();
    // Ovde bi trebalo implementirati i18n logiku
  };

  const menuItems: MenuItemType[] = [
    { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
    {
      text: 'Tickets Management',
      icon: <Event />,
      path: '/event-tickets/venues',
      feature: 'events_enabled',
      hasSubmenu: true,
      subItems: [
        { text: 'Mesta održavanja', icon: <LocationOn />, path: '/event-tickets/venues', feature: 'events_enabled' },
        { text: 'Događaji', icon: <Event />, path: '/event-tickets/events', feature: 'events_enabled' },
        { text: 'Karte', icon: <ConfirmationNumber />, path: '/event-tickets/tickets', feature: 'events_enabled' }
      ]
    },
    {
      text: 'Membership Management',
      icon: <CardMembership />,
      path: '/membership/members',
      feature: 'membership_enabled',
      hasSubmenu: true,
      subItems: [
        { text: 'Članovi', icon: <Person />, path: '/membership/members', feature: 'membership_enabled' },
        { text: 'Tiers', icon: <Stars />, path: '/membership/tiers', feature: 'membership_enabled' }
      ]
    },
    {
      text: 'Coupon Management',
      icon: <LocalOffer />,
      path: '/coupons/offers',
      feature: 'coupons_enabled',
      hasSubmenu: true,
      subItems: [
        { text: 'Ponude', icon: <LocalOffer />, path: '/coupons/offers', feature: 'coupons_enabled' },
        { text: 'Kuponi', icon: <ConfirmationNumber />, path: '/coupons/coupons', feature: 'coupons_enabled' }
      ]
    },
    { text: 'Lokacije', icon: <LocationOn />, path: '/locations' },
    { text: 'Analitika', icon: <Analytics />, path: '/analytics' },
    { text: 'GDPR Management', icon: <Security />, path: '/gdpr-management' },
    {
      text: 'Admin',
      icon: <Settings />,
      path: '/admin',
      adminOnly: true,
      hasSubmenu: true,
      subItems: [
        { text: 'Kompanije', icon: <Business />, path: '/admin/companies', adminOnly: true },
        { text: 'Korisnici', icon: <Group />, path: '/admin/users', adminOnly: true },
        { text: 'API Ključevi', icon: <VpnKey />, path: '/admin/api-keys', adminOnly: true },
        { text: 'Resursi', icon: <Business />, path: '/resources', adminOnly: true },
        { text: 'Audit logovi', icon: <History />, path: '/admin/audit-logs', adminOnly: true },
        { text: 'Performance', icon: <Stars />, path: '/admin/performance', adminOnly: true }
      ]
    },
    { text: 'Moj Nalog', icon: <Person />, path: '/account' },
    { text: 'Podrška', icon: <ContactSupport />, path: '/support' }
  ];

  const filteredMenuItems = menuItems.filter(item => {
    // Check admin-only items
    if (item.adminOnly && user?.role !== 'admin') {
      return false;
    }

    // Check feature-based items (only for non-admin users)
    if (item.feature && user?.role !== 'admin') {
      return features[item.feature];
    }

    return true;
  }).map(item => {
    // Filter subItems if they exist
    if (item.subItems) {
      const filteredSubItems = item.subItems.filter(subItem => {
        // Check admin-only subitems
        if (subItem.adminOnly && user?.role !== 'admin') {
          return false;
        }

        if (subItem.feature && user?.role !== 'admin') {
          return features[subItem.feature];
        }
        return true;
      });

      return {
        ...item,
        subItems: filteredSubItems
      };
    }

    return item;
  });

  const drawer = (
    <Box>
      <Toolbar sx={{ justifyContent: 'center', py: getSpacing(2, 1.5) }}>
        <Logo
          variant="horizontal"
          colorScheme="light"
          height={isMobile ? 36 : 40}
          onClick={() => navigate('/dashboard')}
        />
      </Toolbar>
      <Divider />
      <List>
        {filteredMenuItems.map((item) => (
          <React.Fragment key={item.text}>
            <ListItem disablePadding>
              <ListItemButton
                disabled={item.disabled}
                selected={!item.hasSubmenu && location.pathname === item.path}
                onClick={() => {
                  if (item.disabled) return;
                  if (item.hasSubmenu) {
                    if (item.text === 'Pass Manager') {
                      handlePassManagerToggle();
                    } else if (item.text === 'Event Management') {
                      handleEventManagerToggle();
                    } else if (item.text === 'Tickets Management') {
                      handleTicketsManagerToggle();
                    } else if (item.text === 'Membership Management') {
                      handleMembershipManagerToggle();
                    } else if (item.text === 'Coupon Management') {
                      handleCouponManagerToggle();
                    } else if (item.text === 'Admin') {
                      handleAdministrationToggle();
                    }
                  } else {
                    navigate(item.path);
                    if (isMobile) {
                      setMobileOpen(false);
                    }
                  }
                }}
                sx={{
                  minHeight: getTouchFriendlySize(48),
                  borderRadius: 2,
                  mx: 1,
                  my: 0.5,
                  opacity: item.disabled ? 0.5 : 1,
                  cursor: item.disabled ? 'not-allowed' : 'pointer',
                  '&.Mui-selected': {
                    backgroundColor: alpha('#fff', 0.15),
                    '&:hover': {
                      backgroundColor: alpha('#fff', 0.2),
                    }
                  },
                  '&:hover': {
                    backgroundColor: alpha('#fff', 0.1),
                  }
                }}
              >
                <ListItemIcon sx={{
                  color: 'white',
                  minWidth: isMobile ? '40px' : '56px'
                }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  sx={{
                    '& .MuiListItemText-primary': {
                      color: 'white',
                      fontSize: isMobile ? '0.95rem' : '1rem',
                      fontWeight: 500
                    }
                  }}
                />
                {item.hasSubmenu && (
                  (() => {
                    if (item.text === 'Pass Manager') {
                      return passManagerOpen ? <ExpandLess /> : <ExpandMore />;
                    } else if (item.text === 'Event Management') {
                      return eventManagerOpen ? <ExpandLess /> : <ExpandMore />;
                    } else if (item.text === 'Tickets Management') {
                      return ticketsManagerOpen ? <ExpandLess /> : <ExpandMore />;
                    } else if (item.text === 'Membership Management') {
                      return membershipManagerOpen ? <ExpandLess /> : <ExpandMore />;
                    } else if (item.text === 'Coupon Management') {
                      return couponManagerOpen ? <ExpandLess /> : <ExpandMore />;
                    } else if (item.text === 'Admin') {
                      return administrationOpen ? <ExpandLess /> : <ExpandMore />;
                    }
                    return null;
                  })()
                )}
              </ListItemButton>
            </ListItem>
            {item.hasSubmenu && item.text === 'Pass Manager' && (
              <Collapse in={passManagerOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {item.subItems?.map((subItem) => subItem && (
                    <ListItemButton
                      key={subItem.text}
                      sx={{
                        pl: 5,
                        '& .MuiListItemText-primary': {
                          fontSize: '0.875rem',
                          fontWeight: 400
                        },
                        '& .MuiListItemIcon-root': {
                          minWidth: '32px'
                        }
                      }}
                      selected={location.pathname === subItem.path}
                      onClick={() => {
                        navigate(subItem.path);
                        if (isMobile) {
                          setMobileOpen(false);
                        }
                      }}
                    >
                      <ListItemIcon sx={{ fontSize: '1.2rem' }}>
                        {subItem.icon}
                      </ListItemIcon>
                      <ListItemText primary={subItem.text} />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            )}
            {item.hasSubmenu && item.text === 'Event Management' && (
              <Collapse in={eventManagerOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {item.subItems?.map((subItem) => subItem && (
                    <ListItemButton
                      key={subItem.text}
                      sx={{
                        pl: 5,
                        '& .MuiListItemText-primary': {
                          fontSize: '0.875rem',
                          fontWeight: 400
                        },
                        '& .MuiListItemIcon-root': {
                          minWidth: '32px'
                        }
                      }}
                      selected={location.pathname === subItem.path}
                      onClick={() => {
                        navigate(subItem.path);
                        if (isMobile) {
                          setMobileOpen(false);
                        }
                      }}
                    >
                      <ListItemIcon sx={{ fontSize: '1.2rem' }}>
                        {subItem.icon}
                      </ListItemIcon>
                      <ListItemText primary={subItem.text} />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            )}
            {item.hasSubmenu && item.text === 'Tickets Management' && (
              <Collapse in={ticketsManagerOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {item.subItems?.map((subItem) => subItem && (
                    <ListItemButton
                      key={subItem.text}
                      sx={{
                        pl: 5,
                        '& .MuiListItemText-primary': {
                          fontSize: '0.875rem',
                          fontWeight: 400
                        },
                        '& .MuiListItemIcon-root': {
                          minWidth: '32px'
                        }
                      }}
                      selected={location.pathname === subItem.path}
                      onClick={() => {
                        navigate(subItem.path);
                        if (isMobile) {
                          setMobileOpen(false);
                        }
                      }}
                    >
                      <ListItemIcon sx={{ fontSize: '1.2rem' }}>
                        {subItem.icon}
                      </ListItemIcon>
                      <ListItemText primary={subItem.text} />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            )}
            {item.hasSubmenu && item.text === 'Membership Management' && (
              <Collapse in={membershipManagerOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {item.subItems?.map((subItem) => subItem && (
                    <ListItemButton
                      key={subItem.text}
                      sx={{
                        pl: 5,
                        '& .MuiListItemText-primary': {
                          fontSize: '0.875rem',
                          fontWeight: 400
                        },
                        '& .MuiListItemIcon-root': {
                          minWidth: '32px'
                        }
                      }}
                      selected={location.pathname === subItem.path}
                      onClick={() => {
                        navigate(subItem.path);
                        if (isMobile) {
                          setMobileOpen(false);
                        }
                      }}
                    >
                      <ListItemIcon sx={{ fontSize: '1.2rem' }}>
                        {subItem.icon}
                      </ListItemIcon>
                      <ListItemText primary={subItem.text} />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            )}
            {item.hasSubmenu && item.text === 'Coupon Management' && (
              <Collapse in={couponManagerOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {item.subItems?.map((subItem) => subItem && (
                    <ListItemButton
                      key={subItem.text}
                      sx={{
                        pl: 5,
                        '& .MuiListItemText-primary': {
                          fontSize: '0.875rem',
                          fontWeight: 400
                        },
                        '& .MuiListItemIcon-root': {
                          minWidth: '32px'
                        }
                      }}
                      selected={location.pathname === subItem.path}
                      onClick={() => {
                        navigate(subItem.path);
                        if (isMobile) {
                          setMobileOpen(false);
                        }
                      }}
                    >
                      <ListItemIcon sx={{ fontSize: '1.2rem' }}>
                        {subItem.icon}
                      </ListItemIcon>
                      <ListItemText primary={subItem.text} />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            )}
            {item.hasSubmenu && item.text === 'Admin' && (
              <Collapse in={administrationOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {item.subItems?.map((subItem) => subItem && (
                    <ListItemButton
                      key={subItem.text}
                      sx={{
                        pl: 5,
                        '& .MuiListItemText-primary': {
                          fontSize: '0.875rem',
                          fontWeight: 400
                        },
                        '& .MuiListItemIcon-root': {
                          minWidth: '32px'
                        }
                      }}
                      selected={location.pathname === subItem.path}
                      onClick={() => {
                        navigate(subItem.path);
                        if (isMobile) {
                          setMobileOpen(false);
                        }
                      }}
                    >
                      <ListItemIcon sx={{ fontSize: '1.2rem' }}>
                        {subItem.icon}
                      </ListItemIcon>
                      <ListItemText primary={subItem.text} />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', overflow: 'hidden', maxWidth: '100vw' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          background: 'linear-gradient(135deg, #0a1525 0%, #14264a 100%)',
          borderRadius: 0,
          border: 'none',
          boxShadow: 'none',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{
              mr: 2,
              display: { md: 'none' },
              minHeight: getTouchFriendlySize(44),
              minWidth: getTouchFriendlySize(44)
            }}
          >
            <MenuIcon />
          </IconButton>

          {/* Logo (vidljiv samo na mobile kada je sidebar zatvoren) */}
          <Box sx={{ display: { xs: 'block', md: 'none' }, mr: 2 }}>
            <Logo variant="icon" colorScheme="light" height={36} />
          </Box>

          {/* Spacer to push buttons to the right */}
          <Box sx={{ flexGrow: 1 }} />

          <Button
            color="inherit"
            onClick={handleLanguageMenuOpen}
            startIcon={<Language />}
            sx={{
              mr: 1,
              textTransform: 'none',
              minHeight: getTouchFriendlySize(40),
              px: isMobile ? 1 : 2
            }}
          >
            {currentLanguage === 'sr' ? 'SR' : 'EN'}
          </Button>

          <IconButton
            color="inherit"
            sx={{
              mr: 1,
              minHeight: getTouchFriendlySize(44),
              minWidth: getTouchFriendlySize(44)
            }}
          >
            <Notifications />
          </IconButton>

          <IconButton
            onClick={handleProfileMenuOpen}
            sx={{
              p: isMobile ? 0.5 : 0,
              minHeight: getTouchFriendlySize(48),
              minWidth: getTouchFriendlySize(48)
            }}
          >
            <Avatar sx={{
              bgcolor: 'secondary.main',
              width: isMobile ? 36 : 40,
              height: isMobile ? 36 : 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {user?.email?.charAt(0).toUpperCase() || 'U'}
            </Avatar>
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            onClick={handleProfileMenuClose}
            disableScrollLock={true}
            slotProps={{
              paper: {
                sx: {
                  borderRadius: '12px',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                  border: '1px solid rgba(0,0,0,0.08)',
                  mt: 1
                }
              }
            }}
          >
            <MenuItem>
              <Box>
                <Typography variant="subtitle2">{user?.email}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {user?.role === 'admin' ? 'Administrator' : 'Korisnik'}
                </Typography>
              </Box>
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={() => navigate('/profile')}
              sx={{
                borderRadius: '8px',
                mx: 1,
                my: 0.5,
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '& .MuiListItemIcon-root': {
                    color: 'white',
                  }
                }
              }}
            >
              <ListItemIcon>
                <Person fontSize="small" />
              </ListItemIcon>
              Profil
            </MenuItem>
            <MenuItem
              onClick={() => navigate('/settings')}
              sx={{
                borderRadius: '8px',
                mx: 1,
                my: 0.5,
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '& .MuiListItemIcon-root': {
                    color: 'white',
                  }
                }
              }}
            >
              <ListItemIcon>
                <Settings fontSize="small" />
              </ListItemIcon>
              Podešavanja
            </MenuItem>
            <MenuItem
              onClick={handleLogout}
              sx={{
                borderRadius: '8px',
                mx: 1,
                my: 0.5,
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'error.main',
                  color: 'white',
                  '& .MuiListItemIcon-root': {
                    color: 'white',
                  }
                }
              }}
            >
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              Odjavi se
            </MenuItem>
          </Menu>

          {/* Language Menu */}
          <Menu
            anchorEl={languageAnchorEl}
            open={Boolean(languageAnchorEl)}
            onClose={handleLanguageMenuClose}
            onClick={handleLanguageMenuClose}
            disableScrollLock={true}
            slotProps={{
              paper: {
                sx: {
                  borderRadius: '12px',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                  border: '1px solid rgba(0,0,0,0.08)',
                  mt: 1
                }
              }
            }}
          >
            <MenuItem
              onClick={() => handleLanguageChange('sr')}
              sx={{
                borderRadius: '8px',
                mx: 1,
                my: 0.5,
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '& .MuiListItemIcon-root': {
                    color: 'white',
                  }
                }
              }}
            >
              <ListItemIcon>
                <Language fontSize="small" />
              </ListItemIcon>
              Srpski
            </MenuItem>
            <MenuItem
              onClick={() => handleLanguageChange('en')}
              sx={{
                borderRadius: '8px',
                mx: 1,
                my: 0.5,
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '& .MuiListItemIcon-root': {
                    color: 'white',
                  }
                }
              }}
            >
              <ListItemIcon>
                <Language fontSize="small" />
              </ListItemIcon>
              English
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              background: 'linear-gradient(180deg, #0a1525 0%, #14264a 100%)',
              borderRadius: 0,
              border: 'none',
              boxShadow: 'none',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              background: 'linear-gradient(180deg, #0a1525 0%, #14264a 100%)',
              borderRadius: 0,
              border: 'none',
              boxShadow: 'none',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 0,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          maxWidth: '100%',
          overflow: 'hidden',
          minWidth: 0
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default React.memo(DashboardLayout);
